/* eslint-disable max-params */
import { useEffect } from "react"

import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { DurableChatTurnCurrentResponse } from "@/api/models"
import type { ChatSlice } from "@/stores/slices/chat-slice"
import type { StoreApi, UseBoundStore } from "zustand"

import { getCurrentDurableChatTurnApi } from "@/api/api"
import { logger } from "@/lib/logger"

import { isTerminalStatus } from "./durable-chat-turn-registry-utils"
import { releaseDurableSessionPlaceholder } from "./durable-session-placeholders"

// Backstop window for the composer's save guard. `isAwaitingSave` is normally
// cleared by the WS `message_saved` event; if that ack never arrives (backend
// save timeout / falsy save) and the turn has already settled, the composer
// would stay locked (ServiceDesk 130507). This is sized ABOVE the backend
// STREAM_SAVE_TIMEOUT_SECONDS (60s) so it never fires while a legitimate save is
// still in flight (which would risk a double-submit).
export const SAVE_GUARD_TIMEOUT_MS = 90_000

// Backstop window for a hydrated durable turn whose transport is stuck
// reconnecting (issue #2513 finding 5). The registry's own HTTP status-poll
// fallback retries indefinitely with backoff, so under normal conditions it
// resolves the turn long before this fires; this is only the last-resort
// bound for a genuinely stuck viewer (e.g. a sustained backend outage) —
// sized in minutes, not seconds, so it never races a legitimate recovery.
export const DURABLE_TURN_RECONNECT_GUARD_TIMEOUT_MS = 300_000

/** Injectable seam for the reconnect guard's re-verify read (R6). Defaults to
 *  the real `GET /turns/current` call; tests substitute a fake so they never
 *  need to mock `fetch`. Same read `use-durable-chat-turn-hydration.ts`'s own
 *  poll already trusts as the turn's liveness source of truth — reused
 *  directly rather than inventing a second one. */
export type DurableTurnLivenessReader = (
  sessionId: string,
  token: string | undefined
) => Promise<DurableChatTurnCurrentResponse | null>

/**
 * Race-free backstop that releases the composer if `isAwaitingSave` gets stuck.
 *
 * It only arms once the active turn has **settled** (`!isStreaming && !isLoading`)
 * with the save ack still outstanding, so it never fires mid-stream and a late
 * `done` from a prior connection can't trip it for a freshly-started turn. If the
 * ack (or any terminal event) clears `isAwaitingSave` first, the timer is torn
 * down by the effect cleanup and never fires.
 *
 * Also arms a second, much longer-window backstop for a hydrated durable
 * turn whose transport is stuck reconnecting (issue #2513 finding 5):
 * neither a WebSocket close without a terminal frame nor a permanently-failed
 * reconnect otherwise has any bound — `isStreaming`/`isAwaitingSave`/a
 * non-null `streamingAnswer` would stay set forever, and this hook's own
 * primary backstop above explicitly refuses to arm while any of those hold.
 *
 * R6 (durable-turn risk review): on a flaky/proxied network the durable
 * registry can fall back to healthy HTTP polling while still reporting
 * `reconnecting: true` — the transport signal this backstop originally
 * trusted unconditionally. Releasing without checking would silently abandon
 * a turn that is genuinely still running server-side (the next send then
 * either 409s or, before f4366d95d, raced a second concurrent legacy turn).
 * So before releasing, this now re-verifies with the same `GET /turns/current`
 * read the hydration poll trusts: only a confirmed-settled (or confirmed
 * superseded) turn is released. A confirmed-still-alive turn — or an
 * inconclusive check (the read itself failed) — leaves the composer locked
 * and tells the user the response may still be finishing elsewhere; a reload
 * reattaches via the ordinary hydration path. This never leaves the user with
 * a silently-abandoned live turn, at the cost of an extra reload in the rare
 * case the check itself is the thing that's broken.
 */
export function useSaveGuardWatchdog(
  store: UseBoundStore<StoreApi<ChatSlice>>,
  timeoutMs: number = SAVE_GUARD_TIMEOUT_MS,
  durableReconnectTimeoutMs: number = DURABLE_TURN_RECONNECT_GUARD_TIMEOUT_MS,
  getToken?: () => string | undefined,
  readLiveness: DurableTurnLivenessReader = getCurrentDurableChatTurnApi
): void {
  const { t } = useTranslation()
  const currentSessionId = store((s) => s.currentSessionId)
  const session = store((s) =>
    s.currentSessionId ? s.sessionStates[s.currentSessionId] : undefined
  )
  const isAwaitingSave = session?.isAwaitingSave ?? false
  const isStreaming = session?.isStreaming ?? false
  const isLoading = session?.isLoading ?? false
  // A genuine in-progress turn keeps streamingAnswer non-null. Gating on it too
  // means a stale `done` from a prior connection — which can flip
  // isStreaming/isLoading false mid-stream — cannot trick the watchdog into
  // releasing the composer during an active turn.
  const hasStreamingAnswer = (session?.streamingAnswer ?? null) !== null
  const durableTurn = session?.durableTurn ?? null
  const durableTurnReconnecting = session?.durableTurnReconnecting ?? false

  useEffect(() => {
    if (!currentSessionId) return
    // Arm only when the turn has genuinely settled but is still awaiting the
    // save ack — never while a stream is in progress.
    if (!isAwaitingSave || isStreaming || isLoading || hasStreamingAnswer) return

    const sessionId = currentSessionId
    const timer = window.setTimeout(() => {
      const states = store.getState().sessionStates
      if (!(sessionId in states)) return
      const ss = states[sessionId]
      if (ss.isAwaitingSave && !ss.isStreaming && !ss.isLoading && ss.streamingAnswer === null) {
        logger.warn(`[chat] save-guard watchdog released a stuck composer (session ${sessionId})`)
        store.getState().updateSession(sessionId, { isAwaitingSave: false, queueRequestId: null })
      }
    }, timeoutMs)

    return () => window.clearTimeout(timer)
  }, [
    store,
    currentSessionId,
    isAwaitingSave,
    isStreaming,
    isLoading,
    hasStreamingAnswer,
    timeoutMs,
  ])

  useEffect(() => {
    if (!currentSessionId || !durableTurn) return
    // Only while the transport itself reports it isn't live — a durable turn
    // that is genuinely still running (subscribed and receiving events) must
    // never be force-released just because it has been open a while.
    if (!durableTurnReconnecting) return

    const sessionId = currentSessionId
    const { turnId, epoch } = durableTurn
    let torndown = false
    const timer = window.setTimeout(() => {
      // R6: confirm the turn is actually settled/superseded server-side
      // before releasing — `reconnecting: true` alone is not proof it's
      // dead, only that this tab's transport thinks it is.
      void readLiveness(sessionId, getToken?.())
        .then((current): boolean => {
          // Alive iff `/turns/current` still names THIS exact turn at a
          // nonterminal status. A different turn (superseded) or no turn at
          // all (204) or a terminal status (settled just before this read)
          // all mean it is safe to release.
          return (
            current !== null &&
            current.turnId === turnId &&
            current.epoch === epoch &&
            !isTerminalStatus(current.status)
          )
        })
        .catch((error: unknown) => {
          // Inconclusive. Fail toward NOT releasing: a stuck composer is
          // recoverable (reload reattaches); silently abandoning a live turn
          // is not — see the module doc.
          logger.warn(
            `[chat] durable-turn reconnect guard re-verify failed for turn ${turnId} (session ${sessionId})`,
            error as Error
          )
          return true
        })
        .then((stillAlive) => {
          if (torndown) return
          const states = store.getState().sessionStates
          if (!(sessionId in states)) return
          const ss = states[sessionId]
          // Only act if this exact turn is still the one stuck reconnecting —
          // a newer turn, or one that has since recovered/terminated, must
          // never be touched by a stale timer armed for an earlier stuck turn.
          if (
            ss.durableTurn?.turnId !== turnId ||
            ss.durableTurn.epoch !== epoch ||
            !ss.durableTurnReconnecting
          ) {
            return
          }
          if (stillAlive) {
            logger.warn(
              `[chat] durable-turn reconnect guard confirmed turn ${turnId} is still live server-side; keeping the composer locked (session ${sessionId})`
            )
            toast.error(
              t("questioninput.durable_turn_reconnect_stuck", {
                defaultValue:
                  "This chat's response may still be finishing elsewhere. Reload the page to reconnect.",
              })
            )
            return
          }
          logger.warn(
            `[chat] durable-turn reconnect guard released a stuck viewer (session ${sessionId}, turn ${turnId})`
          )
          // `stillAlive` is false here only because the re-verify read above
          // CONFIRMED this exact turn is settled/superseded server-side — not
          // a blind local give-up — so it is safe to also do here, right now,
          // what `attachDurableChatTurnToSession`'s own terminal-emit listener
          // would otherwise eventually do once its still-running registry
          // subscription (never torn down by this local release) catches a
          // real terminal snapshot: release the sidebar placeholder and clear
          // the Stop-button's `cancelPending` latch. Both would otherwise stay
          // stuck for however long that catch-up takes, or forever if the
          // page reloads/closes before it happens. Both calls are idempotent
          // against a later real terminal emit doing the same thing again.
          releaseDurableSessionPlaceholder(sessionId, turnId)
          store.getState().updateSession(sessionId, {
            isLoading: false,
            isStreaming: false,
            isAwaitingSave: false,
            durableTurn: null,
            durableTurnReconnecting: false,
            cancelPending: false,
            // `updateSession` shallow-merges. Leaving streamingAnswer /
            // toolApprovals would keep a waiting-approval overlay visible
            // without a durable identity, and a click then falls through to
            // the legacy WebSocket lookup.
            streamingAnswer: null,
            toolApprovals: [],
            toolApprovalSessionId: null,
          })
        })
    }, durableReconnectTimeoutMs)

    return () => {
      torndown = true
      window.clearTimeout(timer)
    }
  }, [
    store,
    currentSessionId,
    durableTurn,
    durableTurnReconnecting,
    durableReconnectTimeoutMs,
    getToken,
    readLiveness,
    t,
  ])
}
